import { useEffect, useMemo, useState } from 'react';
import moment from 'moment-timezone';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { Button, Card, CardBody, Col, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner, Table, UncontrolledTooltip } from 'reactstrap';
import { Eye, Edit, Play, Trash2, Database, Layers, Plus } from 'react-feather';
import { AiOutlineRobot } from 'react-icons/ai';
import { RiStopCircleLine } from 'react-icons/ri';
import Swal from 'sweetalert2';
import {
  BotManagerAuditLog,
  BotManagerTask,
  convertBotTaskToRpa,
  deleteBotTask,
  fetchBotManagerUsers,
  fetchBotTasks,
  fetchTaskAuditLog,
  fetchUserPermissions,
  startBotTask,
  startBotTaskRuntime,
  stopBotTask,
  stopBotTaskRuntime,
  updateBotTask,
} from './store';
import BulkDataEntryModal from './components/BulkDataEntryModal';
import WsServicePath from '../../../Service/websocket_webservice';

const safeDisplay = (value: unknown) => {
  if (value === null || value === undefined || value === '') return 'N/A';
  return String(value);
};

const renderAuditValue = (value: any, fieldName?: string) => {
  if (value === null || value === undefined || value === '') return '—';

  const isStepsField = fieldName && fieldName.toLowerCase().trim() === 'steps';

  if (isStepsField) {
    let stepsArray: any[] = [];
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        stepsArray = normalizeStepsToArray(parsed);
      } catch (e) {
        // Not a JSON string
      }
    } else {
      stepsArray = normalizeStepsToArray(value);
    }

    if (stepsArray && stepsArray.length > 0) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', textAlign: 'left' }}>
          {stepsArray.map((step: any, index: number) => {
            const stepNum = step?.step || (index + 1);
            const promptVal = step?.prompt || step?.description || '';
            return (
              <div key={index} style={{ fontSize: '11px', lineHeight: '1.4' }}>
                <span className="fw-bold" style={{ color: '#4f46e5' }}>Step {stepNum}:</span> {promptVal}
              </div>
            );
          })}
        </div>
      );
    }
  }

  const isDatasetField = fieldName && fieldName.toLowerCase().trim() === 'dataset';

  if (isDatasetField) {
    let datasetRows: any[] = [];

    const extractRows = (val: any): any[] => {
      if (val === null || val === undefined) return [];

      if (typeof val === 'string') {
        const trimmed = val.trim();
        if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
          try {
            const parsed = JSON.parse(trimmed);
            return extractRows(parsed);
          } catch (e) {
            try {
              const formatted = trimmed
                .replace(/'/g, '"')
                .replace(/None/g, 'null')
                .replace(/True/g, 'true')
                .replace(/False/g, 'false');
              const parsed = JSON.parse(formatted);
              return extractRows(parsed);
            } catch (e2) {
              // Not a valid JSON string even after replacement
            }
          }
        }
        return [];
      }

      if (Array.isArray(val)) {
        return val.flatMap(item => {
          if (item && typeof item === 'object') {
            return [item];
          }
          if (typeof item === 'string') {
            try {
              const parsed = JSON.parse(item);
              return typeof parsed === 'object' && parsed !== null ? [parsed] : [];
            } catch (e) {
              return [];
            }
          }
          return [];
        });
      }

      if (typeof val === 'object') {
        return [val];
      }

      return [];
    };

    datasetRows = extractRows(value);

    if (datasetRows && datasetRows.length > 0) {
      const allKeys = Array.from(
        new Set(datasetRows.flatMap((row) => (row && typeof row === 'object' ? Object.keys(row) : [])))
      );

      if (allKeys.length > 0) {
        return (
          <div className="table-responsive" style={{ maxHeight: '200px', overflowY: 'auto', borderRadius: '4px', border: '1px solid #e2e8f0' }}>
            <table className="table table-sm table-bordered mb-0" style={{ fontSize: '11px', minWidth: '350px' }}>
              <thead style={{ backgroundColor: '#f8fafc', position: 'sticky', top: 0 }}>
                <tr>
                  {allKeys.map((key) => (
                    <th key={key} className="px-2 py-1 small text-capitalize" style={{ fontWeight: 600, color: '#334155', borderBottom: '2px solid #e2e8f0' }}>
                      {key.replace(/_/g, ' ')}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {datasetRows.map((row, idx) => (
                  <tr key={idx}>
                    {allKeys.map((key) => {
                      const cellVal = row && typeof row === 'object' ? row[key] : '';
                      return (
                        <td key={key} className="px-2 py-1 text-break" style={{ textAlign: 'left' }}>
                          {cellVal === null || cellVal === undefined || cellVal === '' ? '—' : String(cellVal)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }
    }
  }

  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch (e) {
      return '[Complex Object]';
    }
  }
  return String(value);
};

const normalizeStepsToArray = (steps: unknown): any[] => {
  if (Array.isArray(steps)) return steps;
  if (steps && typeof steps === 'object') return Object.values(steps as Record<string, any>);
  return [];
};

const numberDuplicatePlaceholders = (steps: any[]): any[] => {
  const placeholderCount = new Map<string, number>();
  const regex = /\{+([^{}]+?)\}+/g;

  return steps.map((step) => {
    const prompt = step.prompt || '';
    let newPrompt = prompt;
    const matches: Array<{ match: string; placeholder: string; index: number }> = [];
    let match: RegExpExecArray | null;

    regex.lastIndex = 0;

    while ((match = regex.exec(prompt)) !== null) {
      matches.push({
        match: match[0],
        placeholder: match[1].trim(),
        index: match.index,
      });
    }

    for (let i = matches.length - 1; i >= 0; i--) {
      const { match: originalMatch, placeholder, index } = matches[i];
      const count = placeholderCount.get(placeholder) || 0;
      placeholderCount.set(placeholder, count + 1);

      let replacement: string;
      if (count === 0) {
        replacement = `{${placeholder}}`;
      } else {
        replacement = `{${placeholder}_${count + 1}}`;
      }

      newPrompt = newPrompt.slice(0, index) + replacement + newPrompt.slice(index + originalMatch.length);
    }

    return {
      ...step,
      prompt: newPrompt,
    };
  });
};

interface BotManagerProps {
  isActive?: boolean;
  setActiveTab?: (tab: 'tasks' | 'rpa' | 'assistant' | 'rpa-assistant') => void;
}

const BotManager = ({ isActive = true, setActiveTab }: BotManagerProps) => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<BotManagerTask[]>([]);
  const [users, setUsers] = useState<{ value: string; label: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [sort, setSort] = useState<'asc' | 'desc'>('desc');
  const [sortColumn, setSortColumn] = useState('createdAt');
  const [filterUserId, setFilterUserId] = useState('');
  const [total, setTotal] = useState(0);
  const [selectedTask, setSelectedTask] = useState<BotManagerTask | null>(null);
  const [auditLogs, setAuditLogs] = useState<BotManagerAuditLog[]>([]);
  const [auditOpen, setAuditOpen] = useState(false);
  const [instructionOpen, setInstructionOpen] = useState(false);
  const [bulkDataOpen, setBulkDataOpen] = useState(false);
  const [bulkDataTask, setBulkDataTask] = useState<BotManagerTask | null>(null);
  const [bulkDataMode, setBulkDataMode] = useState<'start' | 'update'>('update');
  const [instructionText, setInstructionText] = useState('');
  const [instructionTitle, setInstructionTitle] = useState('');
  const [instructionDomain, setInstructionDomain] = useState('');
  const [instructionDescription, setInstructionDescription] = useState('');
  const [instructionSteps, setInstructionSteps] = useState<Array<{
    step: number;
    prompt: string;
    action: string;
    tag: string;
    id: string;
    class: string;
    xpath: string;
    input_type: string;
    placeholder: string;
  }>>([]);
  const [rpaConversionOpen, setRpaConversionOpen] = useState(false);
  const [selectedTaskForRpa, setSelectedTaskForRpa] = useState<BotManagerTask | null>(null);
  const [rpaName, setRpaName] = useState('');
  const [convertingAction, setConvertingAction] = useState<string | null>(null);
  const [conversionError, setConversionError] = useState<string | null>(null);
  const [promptRequired, setPromptRequired] = useState(false);
  const [promptMessage, setPromptMessage] = useState('');
  const [showNameInput, setShowNameInput] = useState(false);
  const [runningTaskId, setRunningTaskId] = useState<string | null>(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [menuActions, setMenuActions] = useState<string>('');
  const userId = useMemo(() => localStorage.getItem('id') || '', []);
  const username = useMemo(() => localStorage.getItem('UserName') || '', []);
  const userType = useMemo(() => localStorage.getItem('UserType') || '', []);
  const totalPages = Math.max(1, Math.ceil(total / perPage));

  const loadTasks = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const result = await fetchBotTasks({ q: search, page, perPage, sort, sortColumn, filterUserId });
      setTasks(result.data);
      setTotal(result.count);
      setHasLoaded(true);
    } catch (error: any) {
      Swal.fire('Error', error.message || 'Failed to load bot manager data', 'error');
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const closeBulkDataModal = () => {
    setBulkDataOpen(false);
    setBulkDataTask(null);
  };

  useEffect(() => {
    loadTasks();
  }, [page, perPage, sort, sortColumn, filterUserId]);

  useEffect(() => {
    if (isActive) {
      setRunningTaskId(null);
      loadTasks(false);
    }
  }, [isActive]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(1);
      loadTasks();
    }, 300);
    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    fetchBotManagerUsers().then(setUsers).catch(() => undefined);
  }, [userType]);

  useEffect(() => {
    if (userId) {
      fetchUserPermissions(userId).then((response) => {
        if (Array.isArray(response) && response[0]?.Permission) {
          const action = response[0].Permission.find(
            (item: any) => item.MenuKey === 'BOTMANAGER'
          );
          if (action) {
            setMenuActions(action.MenuActions);
          }
        }
      }).catch(err => console.error('Failed to fetch permissions', err));
    }
  }, [userId]);

  useEffect(() => {
    const wsUrl = `${WsServicePath}ws/userActivity/`;
    const ws = new WebSocket(wsUrl);

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        // console.log('ws',data)
        if (data) {
          const updatedDoc = data.fullDocument;
          const opType = data.operationType;
          const docId = updatedDoc?._id?.$oid || data.documentKey?._id?.$oid || (typeof data.documentKey?._id === 'string' ? data.documentKey?._id : null);

          if (docId) {
            if (opType === 'delete' || (updatedDoc && updatedDoc.status === 'R')) {
              setTasks((prevTasks) => {
                const filtered = prevTasks.filter((t) => t.id !== docId);
                if (filtered.length !== prevTasks.length) {
                  setTotal((prevTotal) => Math.max(0, prevTotal - 1));
                }
                return filtered;
              });
            } else if (updatedDoc && (opType === 'update' || opType === 'replace')) {
              if (updatedDoc.bot_status && updatedDoc.bot_status !== 'I') {
                setRunningTaskId((prev) => (prev === docId ? null : prev));
              }
              setTasks((prevTasks) =>
                prevTasks.map((t) => {
                  if (t.id === docId) {
                    return {
                      ...t,
                      project_title: updatedDoc.project_title ?? t.project_title,
                      Description: updatedDoc.Description ?? t.Description,
                      bot_status: updatedDoc.bot_status ?? t.bot_status,
                      Domain: updatedDoc.initial_url ?? t.Domain,
                      steps: updatedDoc.steps ?? t.steps,
                      RPA_status: updatedDoc.RPA_status ?? t.RPA_status,
                      Dataset: updatedDoc.Dataset ?? t.Dataset,
                      Task_Type: updatedDoc.task_type ?? t.Task_Type,
                      credentialsReusable: updatedDoc.credentialsReusable ?? t.credentialsReusable,
                    };
                  }
                  return t;
                })
              );
            } else {
              loadTasks();
            }
          } else {
            loadTasks();
          }
        }
      } catch (error) {
        console.error('[BotManagerMain] WebSocket message error:', error);
      }
    };

    ws.onerror = (error) => {
      console.error('[BotManagerMain] WebSocket error:', error);
    };

    return () => {
      ws.close();
    };
  }, []);

  const handleAudit = async (task: BotManagerTask) => {
    setSelectedTask(task);
    setAuditOpen(true);
    setAuditLogs([]);
    try {
      const data = await fetchTaskAuditLog(task.id);
      setAuditLogs(data);
    } catch (error: any) {
      Swal.fire('Error', error.message || 'Failed to fetch audit log', 'error');
      setAuditOpen(false);
    }
  };

  const handleDelete = async (task: BotManagerTask) => {
    const result = await Swal.fire({
      title: 'Delete assistant?',
      text: `You are about to delete "${task.project_title}".`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
    });
    if (!result.isConfirmed) return;

    try {
      await deleteBotTask({ id: task.id, name: task.project_title, deletedBy: username });
      await loadTasks();
      Swal.fire('Success', 'Assistant deleted successfully', 'success');
    } catch (error: any) {
      Swal.fire('Error', error.message || 'Failed to delete assistant', 'error');
    }
  };

  const openBulkDataModal = (task: BotManagerTask, mode: 'start' | 'update' = 'update') => {
    setBulkDataTask(task);
    setBulkDataMode(mode);
    setBulkDataOpen(true);
  };

  const handleConvertToRpa = (task: BotManagerTask) => {
    setSelectedTaskForRpa(task);
    setRpaName(task.project_title || '');
    setConversionError(null);
    setPromptRequired(false);
    setPromptMessage('');
    setShowNameInput(false);
    setRpaConversionOpen(true);
  };

  const closeRpaConversionModal = () => {
    if (convertingAction) return;
    setRpaConversionOpen(false);
    setSelectedTaskForRpa(null);
    setRpaName('');
    setConversionError(null);
    setPromptRequired(false);
    setPromptMessage('');
    setShowNameInput(false);
  };

  const handleConfirmRpaConversion = async (action?: string) => {
    if (!selectedTaskForRpa || !rpaName.trim()) return;

    if (action === 'Insert' && rpaName.trim().toLowerCase() === selectedTaskForRpa.project_title?.trim().toLowerCase()) {
      setConversionError('An RPA workflow with this name already exists. Please provide a unique name.');
      Swal.fire({
        title: 'Name Already Exists',
        text: 'An RPA workflow with this name already exists. Please provide a unique name.',
        icon: 'warning',
        confirmButtonText: 'OK',
      });
      return;
    }

    setConvertingAction(action || 'default');
    setConversionError(null);

    try {
      const response = await convertBotTaskToRpa({
        id: selectedTaskForRpa.id,
        name: rpaName.trim(),
        user_id: userId,
        user_name: username,
        action,
      });

      if (response?.success && response.prompt_required) {
        setPromptRequired(true);
        setPromptMessage(response.message || 'Multiple workflows detected. Do you want to replace the existing workflow or insert a new one?');
        setConvertingAction(null);
        return;
      }

      if (!response?.success) {
        throw new Error(response?.message || 'Failed to convert to RPA');
      }

      await loadTasks();
      closeRpaConversionModal();
      Swal.fire({
        title: 'Success',
        text: response.message || 'Converted to RPA successfully',
        icon: 'success',
        confirmButtonText: 'OK',
      }).then(() => {
        if (setActiveTab) {
          setActiveTab('rpa');
        }
        navigate('/bot-manager', { state: { activeTab: 'rpa' } });
      });
    } catch (error: any) {
      const errorMsg = error?.message || 'Conversion failed';
      if (errorMsg.toLowerCase().includes('already exists')) {
        Swal.fire({
          title: 'Name Already Exists',
          text: 'An RPA workflow with this name already exists. Please provide a unique name.',
          icon: 'warning',
          confirmButtonText: 'OK',
        });
      }
      setConversionError(errorMsg);
    } finally {
      setConvertingAction(null);
    }
  };

  const handleStartStop = async (task: BotManagerTask, action: 'start' | 'stop') => {
    try {
      if (action === 'start') {
        if (task.Task_Type === 'Auto') {
          openBulkDataModal(task, 'start');
          return;
        }
        if ((window as any).taskMessagesCache) {
          (window as any).taskMessagesCache.delete(task.id);
        }
        setTasks((prevTasks) =>
          prevTasks.map((t) =>
            t.id === task.id ? { ...t, bot_status: 'I' } : t
          )
        );
        setRunningTaskId(task.id);
        await startBotTask({ userId, username, taskId: task.id, title: task.project_title });
        if (setActiveTab) {
          setActiveTab('assistant');
        }
        navigate('/bot-manager', { state: { activeTab: 'assistant', focusTaskId: task.id }, replace: true });
        try {
          await startBotTaskRuntime({ userId, taskId: task.id });
        } catch {
        }
        loadTasks(false);
      } else {
        await stopBotTask({ userId, username, taskId: task.id, title: task.project_title });
        setRunningTaskId(null);
        try {
          await stopBotTaskRuntime({ userId, taskId: task.id });
        } catch (error) {
          console.error('❌ [UI] Runtime stop failed:', error);
        }

        // Update local tasks state immediately to show bot has stopped ('Y' is yet to start)
        setTasks((prevTasks) =>
          prevTasks.map((t) =>
            t.id === task.id ? { ...t, bot_status: 'Y' } : t
          )
        );

        // Show success popup immediately without waiting for table reload
        Swal.fire('Success', 'Bot stopped successfully', 'success');

        // Reload tasks in background without visual loading state
        loadTasks(false);
      }
    } catch (error: any) {
      if (action === 'start') {
        setRunningTaskId(null);
        setTasks((prevTasks) =>
          prevTasks.map((t) =>
            t.id === task.id ? { ...t, bot_status: 'Y' } : t
          )
        );
      }
      Swal.fire('Error', error.message || `Failed to ${action} bot`, 'error');
    }
  };

  const openInstruction = (task: BotManagerTask) => {
    setSelectedTask(task);
    setInstructionText(task.instruction || '');
    setInstructionTitle(task.project_title || '');
    setInstructionDomain(task.Domain || '');
    setInstructionDescription(task.Description || '');
    const rawSteps = normalizeStepsToArray(task.steps).length > 0
      ? normalizeStepsToArray(task.steps).map((step: any, index: number) => ({
        step: Number(step?.step || index + 1),
        prompt: safeDisplay(step?.prompt || step?.description),
        action: safeDisplay(step?.action),
        tag: safeDisplay(step?.element?.tagname || step?.tag),
        id: safeDisplay(step?.element?.id || step?.id),
        class: safeDisplay(step?.element?.class || step?.className),
        xpath: safeDisplay(step?.element?.xpath || step?.xpath),
        input_type: safeDisplay(step?.element?.input_type || step?.input_type),
        placeholder: safeDisplay(step?.element?.placeholder || step?.placeholder),
      }))
      : [];
    const numberedSteps = numberDuplicatePlaceholders(rawSteps);
    setInstructionSteps(numberedSteps);
    setInstructionOpen(true);
  };

  const selectedTaskSteps = useMemo(() => {
    return instructionSteps;
  }, [instructionSteps]);

  const handleSaveInstruction = async () => {
    if (!selectedTask) return;
    try {
      const nextSteps = selectedTaskSteps.map((step, index) => {
        const originalSteps = normalizeStepsToArray(selectedTask.steps);
        const originalStep = originalSteps[index] || {};
        return {
          ...originalStep,
          step: index + 1,
          action: step.action,
          prompt: step.prompt,
          element: {
            id: step.id,
            class: step.class,
            tagname: step.tag,
            xpath: step.xpath,
            input_type: step.input_type,
            placeholder: step.placeholder,
          },
        };
      });

      const numberedSteps = numberDuplicatePlaceholders(nextSteps);

      await updateBotTask({
        id: selectedTask.id,
        project_title: instructionTitle,
        Domain: instructionDomain,
        Description: instructionDescription,
        steps: numberedSteps,
        dataset: selectedTask.Dataset || [],
        UserId: userId,
        Username: username,
        Before: {
          project_title: selectedTask.project_title || '',
          Domain: selectedTask.Domain || '',
          Description: selectedTask.Description || '',
          steps: normalizeStepsToArray(selectedTask.steps),
          dataset: selectedTask.Dataset || [],
        },
        After: {
          project_title: instructionTitle,
          Domain: instructionDomain,
          Description: instructionDescription,
          steps: numberedSteps,
          dataset: selectedTask.Dataset || [],
        },
      });

      setTasks((prevTasks) =>
        prevTasks.map((t) =>
          t.id === selectedTask.id
            ? {
              ...t,
              project_title: instructionTitle,
              Domain: instructionDomain,
              Description: instructionDescription,
              steps: numberedSteps,
            }
            : t
        )
      );
      setInstructionOpen(false);
      Swal.fire('Success', 'Bot updated successfully', 'success');
    } catch (error: any) {
      Swal.fire('Error', error.message || 'Failed to save instruction', 'error');
    }
  };

  const handleAddStep = (index: number) => {
    const newStep = {
      step: index + 2,
      prompt: '',
      action: '',
      tag: '',
      id: '',
      class: '',
      xpath: '',
      input_type: '',
      placeholder: ''
    };
    setInstructionSteps((previous) => {
      const newSteps = [...previous];
      newSteps.splice(index + 1, 0, newStep);
      return newSteps.map((s, i) => ({ ...s, step: i + 1 }));
    });
  };

  const handleDeleteStep = (index: number) => {
    setInstructionSteps((previous) => {
      const newSteps = previous.filter((_, i) => i !== index);
      return newSteps.map((s, i) => ({ ...s, step: i + 1 }));
    });
  };

  const handleStepPromptChange = (index: number, value: string) => {
    setInstructionSteps((previous) => previous.map((step, currentIndex) => (
      currentIndex === index ? { ...step, prompt: value } : step
    )));
  };

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSort((previous) => (previous === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSort('asc');
    }
  };

  const getSortIndicator = (column: string) => {
    if (sortColumn !== column) return <span className='ms-1 text-muted'>↕</span>;
    return <span className='ms-1'>{sort === 'asc' ? '↑' : '↓'}</span>;
  };
  return (
    <Card className='border-0 shadow-sm'>
      <CardBody className='p-4'>
        <div className='d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3'>
          <div className='d-flex align-items-center gap-3'>
            <h4 className='mb-0 d-flex align-items-center gap-2'>
              <AiOutlineRobot size={24} />
              IPA Bot Management
            </h4>
            {menuActions.includes('add') && (
              <Button
                color='primary'
                size='sm'
                className='d-flex align-items-center gap-2 rounded-pill px-3'
                onClick={() => navigate('/user-activity-stream')}
                style={{ fontSize: '12px', fontWeight: 600, height: '32px' }}
              >
                <Plus size={14} /> Create New Insurance (IPA)
              </Button>
            )}
          </div>
          <div className='d-flex flex-column flex-md-row align-items-md-center gap-3'>
            {userType === 'MOS' && (
              <div style={{ minWidth: 220 }}>
                <Label className='mb-1 small text-muted'>Select User</Label>
                <Select
                  options={users}
                  value={users.find((option) => option.value === filterUserId) || null}
                  onChange={(selectedOption: any) => setFilterUserId(selectedOption?.value || '')}
                  isSearchable
                  isClearable
                  placeholder='All Users'
                  styles={{
                    control: (styles) => ({
                      ...styles,
                      minHeight: '38px',
                      borderColor: '#cbd5e1',
                      boxShadow: 'none',
                      '&:hover': {
                        borderColor: '#94a3b8'
                      }
                    }),
                    option: (styles, { isFocused, isSelected }) => ({
                      ...styles,
                      backgroundColor: isSelected ? '#3b82f6' : isFocused ? '#f1f5f9' : undefined,
                      color: isSelected ? '#ffffff' : '#1f2937'
                    })
                  }}
                />
              </div>
            )}
            <div style={{ minWidth: 240 }}>
              <Label className='mb-1 small text-muted'>Search</Label>
              <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder='Search by Assistant...' />
            </div>
          </div>
        </div>

        <div className='table-responsive'>
          <Table borderless hover className='align-middle mb-0'>
            <thead style={{ background: 'linear-gradient(to right, #f5f3ff, #faf5ff)' }}>
              <tr className='text-uppercase small'>
                <th className='px-4 py-3' style={{ cursor: 'pointer' }} onClick={() => handleSort('project_title')}>Bot name {getSortIndicator('project_title')}</th>
                <th className='px-4 py-3' style={{ cursor: 'pointer' }} onClick={() => handleSort('Description')}>Description {getSortIndicator('Description')}</th>
                <th className='px-4 py-3' style={{ cursor: 'pointer' }}>Username </th>
                <th className='px-4 py-3'>Bot Type</th>
                <th className='px-4 py-3 text-center'>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={4} className='text-center py-4'><Spinner size='sm' /> Loading...</td></tr>
              ) : tasks.length === 0 ? (
                <tr><td colSpan={4} className='text-center py-4'>No Assistant Data</td></tr>
              ) : tasks.map((task) => (
                <tr key={task.id} className='border-bottom'>
                  <td className='px-4 py-3 fw-medium'>{task.project_title}</td>
                  <td className='px-4 py-3'>{task.Description || '—'}</td>
                  <td className='px-4 py-3'>{task.Username || '—'}</td>
                  <td className='px-4 py-3'>{task.Task_Type === 'Auto' ? 'Automation' : task.Task_Type || '—'}</td>
                  <td className='px-4 py-3 text-center'>
                    <div className='d-inline-flex align-items-center justify-content-center flex-wrap gap-3'>
                      {task.bot_status !== 'I' ? (
                        <button
                          id={`start-bot-${task.id}`}
                          type='button'
                          title={task.bot_status === 'Y' || task.bot_status === 'E' ? 'Start Bot' : 'Bot not available'}
                          onClick={() => handleStartStop(task, 'start')}
                          disabled={!(task.bot_status === 'Y' || task.bot_status === 'E')}
                          className={`btn btn-link p-1 border-0 shadow-none ${task.bot_status === 'Y' || task.bot_status === 'E' ? 'text-success' : 'text-muted opacity-50 cursor-not-allowed'}`}
                        >
                          <Play size={18} />
                        </button>
                      ) : (
                        <button
                          id={`stop-bot-${task.id}`}
                          type='button'
                          title='Stop Bot'
                          onClick={() => handleStartStop(task, 'stop')}
                          className='btn btn-link p-1 border-0 shadow-none text-danger'
                        >
                          <RiStopCircleLine size={18} />
                        </button>
                      )}
                      {menuActions.includes('edit') && (
                        <button
                          id={`edit-bot-${task.id}`}
                          type='button'
                          title='Edit Bot'
                          onClick={() => openInstruction(task)}
                          className='btn btn-link p-1 border-0 shadow-none text-primary'
                        >
                          <Edit size={18} />
                        </button>
                      )}
                      {task.Task_Type === 'Auto' && menuActions.includes('edit') && (
                        <button
                          id={`bulk-entry-${task.id}`}
                          type='button'
                          title='Bulk Data Entry'
                          onClick={() => openBulkDataModal(task)}
                          className='btn btn-link p-1 border-0 shadow-none text-success'
                        >
                          <Database size={18} />
                        </button>
                      )}
                      {menuActions.includes('add') && (
                        <button
                          id={`convert-rpa-${task.id}`}
                          type='button'
                          title={task.RPA_status === 'A'
                            ? 'Convert to RPA'
                            : 'Once the user runs the bot successfully, the RPA Convert option will be enabled'}
                          onClick={(event) => {
                            event.stopPropagation();
                            if (task.RPA_status === 'A') {
                              handleConvertToRpa(task);
                            }
                          }}
                          className={`btn btn-link p-1 border-0 shadow-none ${task.RPA_status === 'A' ? '' : 'text-muted opacity-50'}`}
                          style={{
                            color: task.RPA_status === 'A' ? '#7c3aed' : undefined,
                            cursor: task.RPA_status === 'A' ? 'pointer' : 'not-allowed',
                          }}
                        >
                          <Layers size={18} />
                        </button>
                      )}
                      {menuActions.includes('delete') && (
                        <button
                          id={`delete-bot-${task.id}`}
                          type='button'
                          title='Delete Bot'
                          onClick={() => handleDelete(task)}
                          className='btn btn-link p-1 border-0 shadow-none text-danger'
                        >
                          <Trash2 size={18} />
                        </button>
                      )}
                      <button
                        id={`audit-log-${task.id}`}
                        type='button'
                        title='Audit Log'
                        onClick={() => handleAudit(task)}
                        className='btn btn-link p-1 border-0 shadow-none'
                        style={{ color: '#8b5cf6' }}
                      >
                        <Eye size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>

        <div className='d-flex flex-column flex-md-row justify-content-between align-items-md-center mt-4 gap-3'>
          <div className='d-flex align-items-center gap-2'>
            <span>Show</span>
            <Input type='select' value={perPage} onChange={(event) => { setPerPage(Number(event.target.value)); setPage(1); }} style={{ width: 90 }}>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </Input>
            <span>entries</span>
          </div>
          <div>Showing {Math.min((page - 1) * perPage + 1, total)} to {Math.min(page * perPage, total)} of {total} results</div>
          <div className='d-flex align-items-center gap-2'>
            <Button color='light' disabled={page <= 1} onClick={() => setPage((previous) => Math.max(1, previous - 1))}>Previous</Button>
            <span className='fw-semibold'>{page} / {totalPages}</span>
            <Button color='light' disabled={page >= totalPages} onClick={() => setPage((previous) => Math.min(totalPages, previous + 1))}>Next</Button>
          </div>
        </div>
      </CardBody>

      <Modal
        style={{ maxWidth: '1500px', maxHeight: '600px' }}
        isOpen={auditOpen}
        toggle={() => setAuditOpen(false)}
        className='modal-dialog-centered modal-lg'
      >
        <ModalHeader toggle={() => setAuditOpen(false)}>
          Audit Log for {selectedTask?.project_title}
        </ModalHeader>
        <ModalBody>
          <div style={{ maxHeight: '600px', overflow: 'auto' }}>
            {auditLogs.length > 0 ? (
              <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f2f2f2' }}>
                    <th className="audit-tableHeaderStyle px-3 py-2 text-start">Date & Time</th>
                    <th className="audit-tableHeaderStyle px-3 py-2 text-start">Action</th>
                    <th className="audit-tableHeaderStyle px-3 py-2 text-start">Data Set</th>
                    <th className="audit-tableHeaderStyle px-3 py-2 text-start">Changed By</th>
                  </tr>
                </thead>
                <tbody style={{ alignItems: 'center' }}>
                  {auditLogs
                    .sort((a, b) => new Date(b.Modified).getTime() - new Date(a.Modified).getTime())
                    .map((item, index) => {
                      const hasBeforeAfter = item.Action === 'EDIT' && Array.isArray(item.JsonData) &&
                        item.JsonData.some((d: any) => 'before' in d || 'after' in d);
                      return (
                        <tr key={index} className="border-bottom">
                          <td className="audit-tableDataStyle px-3 py-2">
                            {moment.utc(item.Modified).tz(moment.tz.guess()).format('MM/DD/YYYY hh:mm:ss A')}
                          </td>
                          <td className="audit-tableDataStyle px-3 py-2">
                            <span className="badge bg-light text-dark border">{item.Action}</span>
                          </td>
                          <td className="audit-tableDataStyle px-3 py-2">
                            {item.Action === 'VIEW' ? (
                              <span>{String(item.JsonData)}</span>
                            ) : item.Action === 'DELETE' ? (
                              <span className="text-danger">{String(item.JsonData)}</span>
                            ) : (
                              <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                                <thead>
                                  <tr style={{ backgroundColor: '#f9fafb' }}>
                                    <th className="audit-tableHeaderStyle px-2 py-1 text-start small">Field</th>
                                    {hasBeforeAfter ? (
                                      <>
                                        <th className="audit-tableHeaderStyle px-2 py-1 text-start small">Before</th>
                                        <th className="audit-tableHeaderStyle px-2 py-1 text-start small">After</th>
                                      </>
                                    ) : (
                                      <th className="audit-tableHeaderStyle px-2 py-1 text-start small">Value</th>
                                    )}
                                  </tr>
                                </thead>
                                <tbody>
                                  {Array.isArray(item.JsonData) ? (
                                    item.JsonData
                                      .sort((a, b) => (a.field || '').localeCompare(b.field || ''))
                                      .map((dataItem, dataIndex) => (
                                        <tr key={dataIndex}>
                                          <td className="audit-tableDataStyle px-2 py-1 small">{dataItem.field || dataItem.Field || '—'}</td>
                                          {hasBeforeAfter ? (
                                            <>
                                              <td className="audit-tableDataStyle px-2 py-1 small">{renderAuditValue(dataItem.before, dataItem.field || dataItem.Field)}</td>
                                              <td className="audit-tableDataStyle px-2 py-1 small">{renderAuditValue(dataItem.after, dataItem.field || dataItem.Field)}</td>
                                            </>
                                          ) : (
                                            <td className="audit-tableDataStyle px-2 py-1 small">{renderAuditValue(dataItem.value ?? dataItem.Value, dataItem.field || dataItem.Field)}</td>
                                          )}
                                        </tr>
                                      ))
                                  ) : (
                                    <tr>
                                      <td colSpan={hasBeforeAfter ? 3 : 2} className="audit-tableDataStyle px-2 py-1 small text-break">
                                        {typeof item.JsonData === 'object' ? JSON.stringify(item.JsonData, null, 2) : String(item.JsonData)}
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            )}
                          </td>
                          <td className="audit-tableDataStyle px-3 py-2">
                            <span className="fw-medium text-primary">{item.UserName}</span>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            ) : (
              <div className="text-center py-4 text-muted">No data available</div>
            )}
          </div>
        </ModalBody>
      </Modal>

      <BulkDataEntryModal
        isOpen={bulkDataOpen}
        toggle={closeBulkDataModal}
        task={bulkDataTask}
        userType={userType}
        userId={userId}
        username={username}
        mode={bulkDataMode}
        onSuccess={(updatedDataset) => {
          if (bulkDataTask && updatedDataset) {
            setTasks((prev) =>
              prev.map((t) =>
                t.id === bulkDataTask.id ? { ...t, Dataset: updatedDataset } : t
              )
            );
          }
        }}
        setActiveTab={setActiveTab}
      />

      <Modal isOpen={rpaConversionOpen} toggle={closeRpaConversionModal} centered>
        <ModalHeader toggle={closeRpaConversionModal}>Convert to RPA</ModalHeader>
        <ModalBody>
          <div>
            <p className='text-center mb-4'>
              {promptRequired
                ? (promptMessage || 'Multiple workflows detected. Choose how to continue.')
                : 'This will create a new RPA bot. The original bot will remain available for future edits.'
              }
            </p>

            {(!promptRequired || showNameInput) && (
              <div className='mb-3'>
                <Label className='small fw-semibold'>New RPA Bot Name</Label>
                <Input
                  value={rpaName}
                  onChange={(event) => setRpaName(event.target.value)}
                  placeholder='Enter name...'
                  autoFocus
                  disabled={!!convertingAction}
                />
                {conversionError ? <div className='text-danger small mt-2'>{conversionError}</div> : null}
              </div>
            )}

            <div className='d-flex gap-2 mt-4'>
              {promptRequired && showNameInput ? (
                <Button color='danger' className='flex-grow-1' onClick={() => setShowNameInput(false)} disabled={!!convertingAction}>Back</Button>
              ) : (
                <Button color='danger' className='flex-grow-1' onClick={closeRpaConversionModal} disabled={!!convertingAction}>Cancel</Button>
              )}

              {!promptRequired ? (
                <Button color='primary' className='flex-grow-1' onClick={() => handleConfirmRpaConversion()} disabled={!rpaName.trim() || !!convertingAction}>
                  {convertingAction === 'default' ? 'Converting...' : 'Start Conversion'}
                </Button>
              ) : !showNameInput ? (
                <>
                  <Button color='primary' className='flex-grow-1' onClick={() => handleConfirmRpaConversion('Replace')} disabled={!rpaName.trim() || !!convertingAction}>
                    {convertingAction === 'Replace' ? 'Converting...' : 'Replace Existing'}
                  </Button>
                  <Button color='primary' className='flex-grow-1' onClick={() => setShowNameInput(true)} disabled={!!convertingAction}>
                    Insert New
                  </Button>
                </>
              ) : (
                <Button color='primary' className='flex-grow-1' onClick={() => handleConfirmRpaConversion('Insert')} disabled={!rpaName.trim() || !!convertingAction}>
                  {convertingAction === 'Insert' ? 'Converting...' : 'Confirm Insert'}
                </Button>
              )}
            </div>
          </div>
        </ModalBody>
      </Modal>

      <Modal isOpen={instructionOpen} toggle={() => setInstructionOpen(false)} size='xl' centered contentClassName='border-0 shadow'>
        <ModalHeader toggle={() => setInstructionOpen(false)}>
          Generated Instructions
        </ModalHeader>
        <ModalBody>
          <div className='p-3' style={{ background: '#f8fafc', borderRadius: 12 }}>
            <div className='mx-auto' style={{ maxWidth: 1100 }}>
              <div className='mb-4'>
                <div className='mb-3'>
                  <Label className='fw-semibold small'>Bot Name <span className='text-danger'>*</span></Label>
                  <Input
                    value={instructionTitle}
                    onChange={(event) => setInstructionTitle(event.target.value)}
                    style={{ background: '#ffffff', color: '#111827', border: '1px solid #e5e7eb', borderRadius: 8 }}
                  />
                </div>
                <Row className='g-3'>
                  <Col md={6}>
                    <Label className='fw-semibold small'>Website URL <span className='text-danger'>*</span></Label>
                    <Input
                      value={instructionDomain}
                      onChange={(event) => setInstructionDomain(event.target.value)}
                      onBlur={(event) => {
                        const value = event.target.value.trim();
                        if (value && !value.startsWith('http://') && !value.startsWith('https://')) {
                          setInstructionDomain(`https://${value}`);
                        }
                      }}
                      style={{ background: '#ffffff', color: '#111827', border: '1px solid #e5e7eb', borderRadius: 8 }}
                    />
                  </Col>
                  <Col md={6}>
                    <Label className='fw-semibold small'>Description <span className='text-danger'>*</span></Label>
                    <Input
                      value={instructionDescription}
                      onChange={(event) => setInstructionDescription(event.target.value)}
                      style={{ background: '#ffffff', color: '#111827', border: '1px solid #e5e7eb', borderRadius: 8 }}
                    />
                  </Col>
                </Row>
              </div>

              <div className='mb-3 fw-bold' style={{ fontSize: '14px', color: '#1f2937' }}>
                Step's Count : {selectedTaskSteps.length}
              </div>

              <div className='d-flex flex-column align-items-stretch' style={{ gap: 0, maxHeight: '52vh', overflowY: 'auto', paddingRight: '8px' }}>
                {selectedTaskSteps.map((step, index) => (
                  <div key={`${step.step}-${index}`}>
                    <div className='d-flex align-items-center my-2'>
                      <div className='flex-grow-1' style={{ borderTop: '1px dashed #d1d5db' }}></div>
                      <button
                        className='mx-2 btn btn-link p-0 d-flex align-items-center justify-content-center bg-white'
                        style={{
                          width: 24,
                          height: 24,
                          borderRadius: 4,
                          border: '1px solid #d1d5db',
                          color: '#6b7280',
                          textDecoration: 'none'
                        }}
                        onClick={() => handleAddStep(index - 1)}
                        title='Add Step Before'
                      >
                        <Plus size={14} />
                      </button>
                      <div className='flex-grow-1' style={{ borderTop: '1px dashed #d1d5db' }}></div>
                    </div>

                    <div className='w-100'>
                      <div
                        className='p-3 rounded'
                        style={{
                          background: '#ffffff',
                          border: '1px solid #e5e7eb',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                          width: '100%'
                        }}
                      >
                        <div className='d-flex justify-content-between align-items-center mb-2'>
                          <span className='fw-bold' style={{ color: '#111827', fontSize: '14px' }}>Step {index + 1}</span>
                          <button
                            className='btn btn-link p-0 text-danger'
                            style={{ opacity: 0.8 }}
                            onClick={() => handleDeleteStep(index)}
                            title='Delete Step'
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>

                        <div className='flex-grow-1'>
                          <Label className='small fw-semibold text-muted mb-1'>Prompt</Label>
                          <Input
                            type='textarea'
                            rows={3}
                            value={step.prompt}
                            onChange={(event) => handleStepPromptChange(index, event.target.value)}
                            style={{
                              borderRadius: 8,
                              border: '1px solid #d1d5db',
                              background: '#ffffff',
                              minHeight: '90px',
                              resize: 'vertical',
                              fontSize: '13px'
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    {index === selectedTaskSteps.length - 1 && (
                      <div className='d-flex align-items-center my-2'>
                        <div className='flex-grow-1' style={{ borderTop: '1px dashed #d1d5db' }}></div>
                        <button
                          className='mx-2 btn btn-link p-0 d-flex align-items-center justify-content-center bg-white'
                          style={{
                            width: 24,
                            height: 24,
                            borderRadius: 4,
                            border: '1px solid #d1d5db',
                            color: '#6b7280',
                            textDecoration: 'none'
                          }}
                          onClick={() => handleAddStep(index)}
                          title='Add Step After'
                        >
                          <Plus size={14} />
                        </button>
                        <div className='flex-grow-1' style={{ borderTop: '1px dashed #d1d5db' }}></div>
                      </div>
                    )}
                  </div>
                ))}

                {selectedTaskSteps.length === 0 && (
                  <div className='text-center py-4 text-muted bg-white rounded' style={{ border: '1px solid #e5e7eb' }}>
                    No steps defined for this assistant.
                    <div className='mt-2'>
                      <Button color='primary' size='sm' onClick={() => handleAddStep(-1)}>
                        <Plus size={14} /> Add First Step
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </ModalBody>
        <div className='d-flex justify-content-end gap-2 p-3 pt-0' style={{ background: '#ffffff' }}>
          {selectedTask?.Task_Type === 'Auto' && (
            <Button color='light' style={{ minWidth: 140 }} onClick={() => openBulkDataModal(selectedTask!)}>
              Enter Bulk Data
            </Button>
          )}
          <Button color='light' style={{ minWidth: 110 }} onClick={() => setInstructionOpen(false)}>
            Close
          </Button>
          <Button color='primary' style={{ minWidth: 150 }} onClick={handleSaveInstruction}>
            Save Instructions
          </Button>
        </div>
      </Modal>
    </Card>
  );
};

export default BotManager;
